// Helpdesk Phase 3 — in-product feedback widget (app-wide edge-tab launcher).
//
// GREENFIELD APP — part of src/content/applications/Helpdesk/ only. Do NOT
// import from or modify src/content/applications/Tickets/ or .../WorkLog/.
//
// Mounted once, app-wide, in App.tsx. Renders:
//   - >= md + fine pointer: a right-edge tab launcher (docs/design_handoff_helpdesk/
//     README.md §5) that opens a 420px right drawer.
//   - < md OR coarse pointer: the pre-existing 52px corner FAB, opening a
//     full-height bottom-sheet variant of the same panel.
// Both open the same 4-step panel (Compose -> Consent -> Preview & redact ->
// Confirm & send) and submit through the same, unchanged
// POST /v1/helpdesk/widget/tickets call.
//
// FOLLOW-UPS (deliberately NOT built in this pass):
// - Element/CSS-selector picker ("click to point at the broken thing") is
//   deferred. Only free-text subject/body + auto-captured page context ship
//   in this v1.
// - Automatic viewport capture (an html2canvas-class dependency) is OUT OF
//   SCOPE per README §"Answers to the four open questions" Q3 — this module's
//   CLAUDE.md forbids adding that dependency. "Add a screenshot of this page"
//   opens a manual file picker (accept="image/*"); it does NOT capture the
//   viewport. The consent -> preview -> redact -> confirm flow is built over
//   that existing manual-upload path (AssetAPI.upload), ready for automatic
//   capture to slot in behind it later.
//
// CONTRACT GAP (read before touching consent/confirm copy):
// The design (README §5 "Screenshot governance") asks the consent screen to
// name *this page*, *this desk*, and *this desk's type* (red treatment for
// vendor/external desks). There is exactly one widget endpoint,
// POST /v1/helpdesk/widget/tickets, and CreateWidgetTicketRequest deliberately
// omits deskId/channel — desk resolution is server-side and there is no GET
// to look it up client-side. So this file names the PAGE (route, which we do
// have) and states the risk in generic, honest terms ("the support desk",
// "agents on that desk"), and never renders a desk-type-specific (e.g. red
// vendor/external) treatment on a guess. The acknowledgement itself IS
// expressible: CreateWidgetTicketRequest.context is Record<string, unknown>,
// so a single `screenshotReviewed: true` key is added to the *existing*
// context object when a screenshot is attached and confirmed — nothing else
// about the submit payload changes.
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Checkbox,
  Drawer,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FeedbackIcon from '@mui/icons-material/Feedback';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CropSquareOutlinedIcon from '@mui/icons-material/CropSquareOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from 'src/hooks/useAuth';
import { useConfirmDialog } from 'src/hooks/useConfirmDialog';
import { AssetAPI } from 'src/providers/comments-api';
import { HelpdeskAPI } from 'src/providers/helpdesk-api';
import { HdButton, HdCallout } from '../components';
import ScreenshotRedactor from './ScreenshotRedactor';
import 'src/components/Helpdesk/tokens.css';

interface PendingAttachment {
  file: File;
  assetId: number;
  /** true for the reviewed/redacted screenshot produced by ScreenshotRedactor,
   *  so Confirm & Send can find it and Back/remove can undo just that one. */
  isScreenshot?: boolean;
}

type Step = 1 | 2 | 3 | 4;

async function fetchAppVersion(): Promise<string | undefined> {
  try {
    const res = await fetch(`${process.env.PUBLIC_URL || ''}/meta.json?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return undefined;
    const data = await res.json();
    return typeof data?.version === 'string' ? data.version : undefined;
  } catch {
    return undefined;
  }
}

// Display-only summary of navigator.userAgent (e.g. "Chrome 128 · macOS").
// The actual submitted context.userAgent stays the full raw string, unchanged.
function summarizeUserAgent(ua: string): string {
  const browserMatch = ua.match(/(Chrome|Firefox|Safari|Edg)\/(\d+)/);
  const browser = browserMatch ? `${browserMatch[1] === 'Edg' ? 'Edge' : browserMatch[1]} ${browserMatch[2]}` : 'Unknown browser';
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : 'Unknown OS';
  return `${browser} · ${os}`;
}

// The local Callout that used to live here was the SECOND independent
// hand-rolled copy of the design's Callout (RoutingRuleBuilder had the other).
// Both are now the shared HdCallout — see components/HdCallout.tsx.
function InfoRow({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1.25}>
      <Box
        sx={{
          width: 28,
          height: 28,
          flexShrink: 0,
          borderRadius: 'var(--r-sm)',
          backgroundColor: 'var(--card-sub)',
          border: '1px solid var(--hairline)',
          display: 'grid',
          placeItems: 'center',
          color: 'var(--muted)'
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>{title}</Typography>
        <Typography sx={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.55, mt: 0.25 }}>{desc}</Typography>
      </Box>
    </Stack>
  );
}

export default function FeedbackWidget() {
  const { isAuthenticated } = useAuth();
  const { notifySuccess, notifyError } = useConfirmDialog();
  const location = useLocation();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');
  const isFallback = isCompact || isCoarsePointer;
  const isRtl = theme.direction === 'rtl';

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>(1);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingScreenshotFile, setPendingScreenshotFile] = useState<File | null>(null);
  const [flattenedPreviewUrl, setFlattenedPreviewUrl] = useState<string | null>(null);
  const [screenshotConfirmed, setScreenshotConfirmed] = useState(false);
  const [displayAppVersion, setDisplayAppVersion] = useState<string | undefined>(undefined);

  const screenshotFileInputRef = useRef<HTMLInputElement>(null);
  const genericFileInputRef = useRef<HTMLInputElement>(null);
  const launcherButtonRef = useRef<HTMLButtonElement>(null);

  // Launcher visual state: hover/focus expansion, scroll-dim, and hiding
  // while another MUI Drawer/Dialog/Modal is open anywhere in the app.
  const [tabHovered, setTabHovered] = useState(false);
  const [tabDimmed, setTabDimmed] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  // MUI's Modal (Drawer/Dialog/Menu/etc.) locks body scroll while open by
  // default. Watching that side effect via a body-attribute MutationObserver
  // lets the edge tab hide under ANY overlay in the app -- including its own
  // panel -- without needing a new global "modal open" store, which would be
  // a shared-state change outside this file.
  useEffect(() => {
    if (isFallback) return;
    const check = () => setOverlayOpen(document.body.style.overflow === 'hidden');
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });
    return () => observer.disconnect();
  }, [isFallback]);

  // Dim to 28% after 3s of continuous scrolling; restore on scroll-stop or on
  // pointer coming within 120px of the tab's fixed position (right:0, 58%).
  useEffect(() => {
    if (isFallback) return;
    let dimTimer: number | null = null;
    let stopTimer: number | null = null;
    const onScroll = () => {
      if (stopTimer) window.clearTimeout(stopTimer);
      if (!dimTimer) dimTimer = window.setTimeout(() => setTabDimmed(true), 3000);
      stopTimer = window.setTimeout(() => {
        if (dimTimer) { window.clearTimeout(dimTimer); dimTimer = null; }
        setTabDimmed(false);
      }, 150);
    };
    const onMove = (e: MouseEvent) => {
      const tabX = isRtl ? 0 : window.innerWidth;
      const tabY = window.innerHeight * 0.58;
      const dist = Math.hypot(tabX - e.clientX, tabY - e.clientY);
      if (dist <= 120) setTabDimmed(false);
    };
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('mousemove', onMove);
      if (dimTimer) window.clearTimeout(dimTimer);
      if (stopTimer) window.clearTimeout(stopTimer);
    };
  }, [isFallback, isRtl]);

  // Alt+/ focuses the launcher from anywhere; Escape closes the panel and
  // restores focus to it (Drawer's own onClose already fires on Escape).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.key === '/') {
        e.preventDefault();
        launcherButtonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Restore focus to the launcher when the panel CLOSES — but only then.
  //
  // This previously ran `focus()` whenever `open` was false, which includes the
  // very first run on MOUNT. FeedbackWidget is mounted app-wide in App.tsx, so
  // every page load in the whole product stole focus onto the feedback button:
  // its tooltip popped open unprompted, and keyboard users were dropped on the
  // launcher instead of the page they had just navigated to. wasOpenRef makes
  // it a genuine close transition rather than "not open".
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open) {
      fetchAppVersion().then(setDisplayAppVersion);
    } else if (wasOpenRef.current) {
      launcherButtonRef.current?.focus();
    }
    wasOpenRef.current = open;
    // `open` is the only dependency that matters; re-running on every render
    // would re-fetch constantly.
  }, [open]);

  useEffect(() => {
    return () => {
      if (flattenedPreviewUrl) URL.revokeObjectURL(flattenedPreviewUrl);
    };
  }, [flattenedPreviewUrl]);

  if (!isAuthenticated) return null;
  // Not rendered in the client portal (Q1) -- external users get the
  // portal's own inline "Need help?" entry instead. The unauthenticated CSAT
  // route and public intake forms never reach here since !isAuthenticated
  // already returns null for both.
  if (location.pathname.startsWith('/portal')) return null;

  const resetForm = () => {
    setSubject('');
    setBody('');
    setAttachments([]);
    setStep(1);
    setPendingScreenshotFile(null);
    if (flattenedPreviewUrl) URL.revokeObjectURL(flattenedPreviewUrl);
    setFlattenedPreviewUrl(null);
    setScreenshotConfirmed(false);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setOpen(false);
  };

  // "File" -- a plain attachment, no screenshot consent/redaction involved.
  const handleGenericFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (genericFileInputRef.current) genericFileInputRef.current.value = '';
    if (files.length === 0) return;

    setIsUploading(true);
    try {
      for (const file of files) {
        const assetId = await AssetAPI.upload(file);
        setAttachments((prev) => [...prev, { file, assetId }]);
      }
    } catch (err: any) {
      notifyError('Couldn\'t upload', err?.message || 'Could not attach the file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // "Add a screenshot of this page" -- opens a manual file picker (NOT a
  // viewport capture, see the header comment) and routes the chosen image
  // through consent -> preview & redact before it ever uploads.
  const handleScreenshotFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (screenshotFileInputRef.current) screenshotFileInputRef.current.value = '';
    if (!file) return;
    setPendingScreenshotFile(file);
    setStep(2);
  };

  const handleRemoveAttachment = (assetId: number) => {
    setAttachments((prev) => prev.filter((a) => a.assetId !== assetId));
  };

  const handleConsentDecline = () => {
    setPendingScreenshotFile(null);
    setStep(1);
  };

  const handleRedactorCancel = () => {
    setPendingScreenshotFile(null);
    setScreenshotConfirmed(false);
    setStep(1);
  };

  // The un-redacted bitmap is only ever held by ScreenshotRedactor's own
  // in-memory canvas; this receives just the flattened export.
  const handleFlattened = async (flattened: Blob) => {
    setIsUploading(true);
    try {
      const flattenedFile = new File([flattened], 'screenshot.png', { type: flattened.type || 'image/png' });
      const assetId = await AssetAPI.upload(flattenedFile);
      setAttachments((prev) => [...prev.filter((a) => !a.isScreenshot), { file: flattenedFile, assetId, isScreenshot: true }]);
      if (flattenedPreviewUrl) URL.revokeObjectURL(flattenedPreviewUrl);
      setFlattenedPreviewUrl(URL.createObjectURL(flattened));
      setScreenshotConfirmed(false);
      setStep(4);
    } catch (err: any) {
      notifyError('Couldn\'t upload', err?.message || 'Could not attach the screenshot. Please try again.');
      setStep(1);
    } finally {
      setIsUploading(false);
    }
  };

  const handleBackToRedact = () => {
    setAttachments((prev) => prev.filter((a) => !a.isScreenshot));
    setScreenshotConfirmed(false);
    setStep(3);
  };

  const handleRemoveScreenshot = () => {
    setAttachments((prev) => prev.filter((a) => !a.isScreenshot));
    setPendingScreenshotFile(null);
    setScreenshotConfirmed(false);
    if (flattenedPreviewUrl) URL.revokeObjectURL(flattenedPreviewUrl);
    setFlattenedPreviewUrl(null);
    setStep(1);
  };

  const hasScreenshot = attachments.some((a) => a.isScreenshot);

  const handleSubmit = async () => {
    if (!subject.trim() || isSubmitting || isUploading) return;
    if (hasScreenshot && !screenshotConfirmed) return;

    setIsSubmitting(true);
    try {
      const appVersion = await fetchAppVersion();
      // Unchanged: same fields as before (route, url, viewport, userAgent,
      // optional appVersion). The only addition is `screenshotReviewed`,
      // appended -- not restructured -- and only when a screenshot is
      // actually attached, since that's the one acknowledgement the
      // consent/redact flow is required to record against the ticket.
      const context: Record<string, unknown> = {
        route: location.pathname,
        url: window.location.href,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        userAgent: navigator.userAgent
      };
      if (appVersion) context.appVersion = appVersion;
      if (hasScreenshot) context.screenshotReviewed = true;

      await HelpdeskAPI.submitWidgetTicket({
        subject: subject.trim(),
        body: body.trim() || undefined,
        context,
        attachmentAssetIds: attachments.length ? attachments.map((a) => a.assetId) : undefined
      });

      notifySuccess('Feedback sent', 'Thanks — your ticket has been submitted.');
      resetForm();
      setOpen(false);
    } catch (err: any) {
      notifyError('Couldn\'t submit', err?.message || 'Could not submit your feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const nonScreenshotAttachments = attachments.filter((a) => !a.isScreenshot);
  const contextSample = [
    `page ${location.pathname}`,
    `screen ${window.innerWidth}x${window.innerHeight}`,
    displayAppVersion ? `version ${displayAppVersion}` : null,
    `browser ${summarizeUserAgent(navigator.userAgent)}`
  ].filter(Boolean);

  // ── Panel steps ─────────────────────────────────────────────────────────

  const renderCompose = () => (
    <>
      <Typography sx={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.55 }}>
        Goes to our support desk. We automatically include this page&apos;s context to help us investigate.
      </Typography>
      <TextField
        label="What went wrong?"
        required
        fullWidth
        size="small"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        disabled={isSubmitting}
      />
      <TextField
        label="Anything else we should know?"
        helperText="Steps you took, what you expected to happen. Optional."
        fullWidth
        multiline
        minRows={4}
        size="small"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        disabled={isSubmitting}
      />

      <Box sx={{ border: '1px solid var(--hairline)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1, backgroundColor: 'var(--card-sub)' }}>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'var(--muted)' }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, flex: 1 }}>Sent automatically with your report</Typography>
          <Tooltip title="This page's route, viewport size, app version and browser help us reproduce the issue. It never includes what's on the screen unless you attach a screenshot.">
            <Typography component="span" sx={{ fontSize: 11.5, color: 'var(--accent)', cursor: 'default' }}>
              What&apos;s this?
            </Typography>
          </Tooltip>
        </Stack>
        <Box sx={{ px: 1.5, py: 1, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)', lineHeight: 1.8 }}>
          {contextSample.map((line) => (
            <div key={line as string}>{line}</div>
          ))}
        </Box>
      </Box>

      <Stack direction="row" spacing={1}>
        <input ref={screenshotFileInputRef} type="file" accept="image/*" hidden onChange={handleScreenshotFileSelect} />
        <Box
          component="button"
          type="button"
          onClick={() => screenshotFileInputRef.current?.click()}
          disabled={isUploading || isSubmitting}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1.25,
            borderRadius: 'var(--r-sm)',
            border: '1px dashed var(--hairline-strong)',
            background: 'transparent',
            fontSize: 12.5,
            fontWeight: 500,
            color: 'var(--ink-2)',
            cursor: 'pointer',
            fontFamily: 'inherit',
            '&:disabled': { opacity: 0.5, cursor: 'not-allowed' }
          }}
        >
          <PhotoCameraOutlinedIcon sx={{ fontSize: 15, color: 'var(--muted)' }} />
          {/* Manual picker, not a viewport capture -- see the file header comment. */}
          Add a screenshot of this page
        </Box>
        <input ref={genericFileInputRef} type="file" hidden onChange={handleGenericFileSelect} />
        <Box
          component="button"
          type="button"
          onClick={() => genericFileInputRef.current?.click()}
          disabled={isUploading || isSubmitting}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1.25,
            borderRadius: 'var(--r-sm)',
            border: '1px dashed var(--hairline-strong)',
            background: 'transparent',
            fontSize: 12.5,
            fontWeight: 500,
            color: 'var(--ink-2)',
            cursor: 'pointer',
            fontFamily: 'inherit',
            '&:disabled': { opacity: 0.5, cursor: 'not-allowed' }
          }}
        >
          <AttachFileIcon sx={{ fontSize: 15, color: 'var(--muted)' }} />
          File
        </Box>
      </Stack>

      {(nonScreenshotAttachments.length > 0 || hasScreenshot) && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {attachments.map((a) => (
            <Box
              key={a.assetId}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: 999,
                fontSize: 11.5,
                backgroundColor: 'var(--card-sub)',
                border: '1px solid var(--hairline)'
              }}
            >
              {a.isScreenshot ? 'Screenshot (redacted)' : a.file.name}
              {!isSubmitting && (
                <IconButton size="small" sx={{ p: 0.25 }} onClick={() => (a.isScreenshot ? handleRemoveScreenshot() : handleRemoveAttachment(a.assetId))}>
                  <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Stack>
      )}

      <Typography sx={{ fontSize: 11.5, color: 'var(--muted-2)', lineHeight: 1.5 }}>
        Nothing is captured until you tap &quot;Add a screenshot&quot; -- and even then, not until you pass the review step.
      </Typography>
    </>
  );

  const renderConsent = () => (
    <>
      <HdCallout tone="danger" icon={WarningAmberOutlinedIcon} title="This may show more than you expect">
        {/* GAP: the widget cannot resolve which desk or desk type a ticket
            will land on -- CreateWidgetTicketRequest omits deskId and there
            is no lookup endpoint (see header comment). We name the page,
            which we do have, and describe the risk generically rather than
            inventing a desk name or a vendor/external red treatment. */}
        You&apos;re on <b>{location.pathname}</b>. A screenshot of this page can include names, numbers or other details
        visible on screen right now.
      </HdCallout>
      <Stack spacing={1.5}>
        <InfoRow
          icon={<VisibilityOutlinedIcon sx={{ fontSize: 14 }} />}
          title="You review it first"
          desc="The image opens in an editor. Nothing leaves your browser until you press Send."
        />
        <InfoRow
          icon={<CropSquareOutlinedIcon sx={{ fontSize: 14 }} />}
          title="You can black out anything"
          desc="Draw over any part of the image. Redactions are burned into the picture before upload -- the original is never uploaded."
        />
        <InfoRow
          icon={<PeopleAltOutlinedIcon sx={{ fontSize: 14 }} />}
          title="Who will see it"
          desc="Agents on the support desk that handles this report."
        />
        <InfoRow
          icon={<EventOutlinedIcon sx={{ fontSize: 14 }} />}
          title="How long it is kept"
          desc="Stored with the ticket and deleted with it, per your tenant's attachment retention setting."
        />
      </Stack>
      <Box sx={{ flex: 1 }} />
      <Typography sx={{ fontSize: 11, color: 'var(--muted-2)', lineHeight: 1.5 }}>
        This screen is the consent record for this report.
      </Typography>
    </>
  );

  const renderRedact = () => {
    if (!pendingScreenshotFile) return null;
    return <ScreenshotRedactor file={pendingScreenshotFile} onFlattened={handleFlattened} onCancel={handleRedactorCancel} />;
  };

  const renderConfirm = () => (
    <>
      <Box>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{subject.trim() || 'Untitled report'}</Typography>
        <Typography sx={{ fontSize: 12, color: 'var(--muted)' }}>
          {/* GAP: same desk-resolution limit as the consent screen -- no desk
              name/type is renderable client-side. */}
          Goes to our support desk
        </Typography>
      </Box>

      {hasScreenshot && flattenedPreviewUrl && (
        <Stack direction="row" spacing={1.5} sx={{ p: 1.25, border: '1px solid var(--hairline)', borderRadius: 'var(--r-md)' }}>
          <Box
            component="img"
            src={flattenedPreviewUrl}
            alt="Redacted screenshot preview"
            sx={{ width: 96, height: 66, objectFit: 'cover', borderRadius: '5px', border: '1px solid var(--hairline-strong)' }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>1 screenshot</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'var(--muted)', mt: 0.25, lineHeight: 1.5 }}>
              This is exactly what agents will see.
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }}>
              <HdButton hdSize="xs" onClick={handleBackToRedact} disabled={isSubmitting}>
                Edit
              </HdButton>
              <HdButton hdSize="xs" onClick={handleRemoveScreenshot} disabled={isSubmitting}>
                Remove
              </HdButton>
            </Stack>
          </Box>
        </Stack>
      )}

      {hasScreenshot && (
        <FormControlLabel
          control={
            <Checkbox
              checked={screenshotConfirmed}
              onChange={(e) => setScreenshotConfirmed(e.target.checked)}
              disabled={isSubmitting}
              size="small"
            />
          }
          label={
            <Box>
              <Typography sx={{ fontSize: 12.5 }}>
                I&apos;ve checked the screenshot and it doesn&apos;t show anything the helpdesk shouldn&apos;t see.
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'var(--muted-2)', mt: 0.25 }}>
                Required before a screenshot can be attached. Recorded against the ticket.
              </Typography>
            </Box>
          }
          sx={{ alignItems: 'flex-start', m: 0 }}
        />
      )}

      <Box sx={{ flex: 1 }} />
      <HdCallout tone="neutral" icon={false}>
        Page context (route, screen size, browser, app version) is attached either way -- no personal data.
      </HdCallout>
    </>
  );

  const stepConfig: Record<Step, { title: string; sub?: string }> = {
    1: { title: 'Report a problem', sub: "You'll get an email with the ticket number." },
    2: { title: 'Add a screenshot' },
    3: { title: 'Check the screenshot', sub: 'Black out anything the helpdesk shouldn\'t see.' },
    4: { title: 'Confirm & send' }
  };

  const canSend = !!subject.trim() && !isSubmitting && !isUploading && (!hasScreenshot || screenshotConfirmed);

  const renderFooter = () => {
    if (step === 1) {
      return (
        <>
          <Box sx={{ flex: 1 }} />
          <HdButton onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </HdButton>
          <HdButton hdVariant="primary" onClick={handleSubmit} disabled={!subject.trim() || isSubmitting || isUploading}>
            {isSubmitting ? 'Sending...' : 'Send'}
          </HdButton>
        </>
      );
    }
    if (step === 2) {
      return (
        <>
          <Box sx={{ flex: 1 }} />
          <HdButton onClick={handleConsentDecline}>
            Not now
          </HdButton>
          <HdButton hdVariant="primary" onClick={() => setStep(3)}>
            Continue
          </HdButton>
        </>
      );
    }
    if (step === 3) {
      // ScreenshotRedactor renders its own Undo/Discard/Use-this-image
      // controls via the onFlattened/onCancel callbacks -- no separate
      // footer here to avoid duplicating that UI.
      return null;
    }
    return (
      <>
        <HdButton startIcon={<ArrowBackIcon fontSize="small" />} onClick={handleBackToRedact} disabled={isSubmitting}>
          Back
        </HdButton>
        <Box sx={{ flex: 1 }} />
        <HdButton hdVariant="primary" onClick={handleSubmit} disabled={!canSend}>
          {isSubmitting ? 'Sending...' : 'Send report'}
        </HdButton>
      </>
    );
  };

  const panelContent = (
    <Box className="helpdesk-shell" sx={{ width: isFallback ? '100%' : 420, display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: 'var(--card)' }}>
      {isFallback && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 2, backgroundColor: 'var(--hairline-strong)' }} />
        </Box>
      )}
      <Box sx={{ px: 2.25, pt: isFallback ? 1.25 : 1.75, pb: 1.5, borderBottom: '1px solid var(--hairline)' }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ width: 26, height: 26, borderRadius: '7px', backgroundColor: 'var(--ink)', color: 'var(--card)', display: 'grid', placeItems: 'center' }}>
            <FeedbackIcon sx={{ fontSize: 15 }} />
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{stepConfig[step].title}</Typography>
          <IconButton size="small" onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        {stepConfig[step].sub && (
          <Typography sx={{ fontSize: 12, color: 'var(--muted)', mt: 0.9, lineHeight: 1.55 }}>{stepConfig[step].sub}</Typography>
        )}
        <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }}>
          {[1, 2, 3, 4].map((i) => (
            <Box key={i} sx={{ flex: 1, height: 3, borderRadius: 1, backgroundColor: i < step ? 'var(--ink)' : 'var(--hairline)' }} />
          ))}
        </Stack>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2.25, py: 2, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        {step === 1 && renderCompose()}
        {step === 2 && renderConsent()}
        {step === 3 && renderRedact()}
        {step === 4 && renderConfirm()}
      </Box>

      {renderFooter() && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2.25, py: 1.5, borderTop: '1px solid var(--hairline)', backgroundColor: 'var(--card-sub)' }}>
          {renderFooter()}
        </Stack>
      )}
    </Box>
  );

  return (
    <>
      {isFallback ? (
        <Tooltip title="Report a problem">
          <Box
            component="button"
            ref={launcherButtonRef}
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Report a problem"
            sx={{
              position: 'fixed',
              [isRtl ? 'left' : 'right']: 16,
              // Skipped: the spec also asks for a 76px lift "when the mobile
              // bottom bar is present". MobileBottomNav only exists under
              // ClientPortal/, where this widget is deliberately not
              // rendered (Q1), so there is no case in this app tree where
              // both are on screen together.
              bottom: 'calc(16px + env(safe-area-inset-bottom))',
              width: 52,
              height: 52,
              borderRadius: '50%',
              border: 'none',
              cursor: 'pointer',
              // Literal hex, not var(--ink)/var(--card): this launcher renders
              // app-wide OUTSIDE any .helpdesk-shell wrapper, so the scoped
              // tokens are undefined here and the background was resolving to
              // transparent (the washed-out "inconsistent" hover state).
              backgroundColor: '#0A0A0B', // theme-exempt: app-wide launcher, renders outside .helpdesk-shell; scoped CSS vars are undefined here
              color: '#FFFFFF', // theme-exempt: same — launcher chrome outside any theme scope
              display: overlayOpen ? 'none' : 'grid',
              placeItems: 'center',
              boxShadow: '0 6px 20px rgba(10,10,11,.28)', // theme-exempt: ink-tinted drop shadow for the out-of-scope launcher; no token for this alpha
              zIndex: theme.zIndex.drawer - 1
            }}
          >
            <FeedbackIcon />
          </Box>
        </Tooltip>
      ) : (
        <Tooltip title="Report a problem" placement={isRtl ? 'right' : 'left'}>
          <Box
            component="button"
            ref={launcherButtonRef}
            type="button"
            onClick={() => setOpen(true)}
            onMouseEnter={() => setTabHovered(true)}
            onMouseLeave={() => setTabHovered(false)}
            onFocus={() => setTabHovered(true)}
            onBlur={() => setTabHovered(false)}
            aria-label="Report a problem"
            sx={{
              position: 'fixed',
              [isRtl ? 'left' : 'right']: 0,
              top: '58%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 38,
              height: 38,
              padding: tabHovered ? (isRtl ? '10px 12px 10px 14px' : '10px 14px 10px 12px') : '10px 9px',
              border: 'none',
              cursor: 'pointer',
              // Literal hex, not var(--ink)/var(--card): this launcher renders
              // app-wide OUTSIDE any .helpdesk-shell wrapper, so the scoped
              // tokens are undefined here and the background was resolving to
              // transparent (the washed-out "inconsistent" hover state).
              backgroundColor: '#0A0A0B', // theme-exempt: app-wide launcher, renders outside .helpdesk-shell; scoped CSS vars are undefined here
              color: '#FFFFFF', // theme-exempt: same — launcher chrome outside any theme scope
              borderRadius: isRtl ? '0 8px 8px 0' : '8px 0 0 8px',
              boxShadow: '0 2px 14px rgba(10,10,11,.22)', // theme-exempt: ink-tinted drop shadow for the out-of-scope launcher; no token for this alpha
              opacity: overlayOpen ? 0 : tabDimmed ? 0.28 : 1,
              pointerEvents: overlayOpen ? 'none' : 'auto',
              transition: 'all .16s ease',
              zIndex: theme.zIndex.drawer - 1
            }}
          >
            <FeedbackIcon sx={{ fontSize: 17 }} />
            {tabHovered && (
              <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
                Report a problem
              </Typography>
            )}
          </Box>
        </Tooltip>
      )}

      <Drawer
        anchor={isFallback ? 'bottom' : isRtl ? 'left' : 'right'}
        open={open}
        onClose={handleClose}
        PaperProps={{
          className: 'helpdesk-shell',
          sx: isFallback
            ? { height: '92%', borderTopLeftRadius: 'var(--r-lg)', borderTopRightRadius: 'var(--r-lg)' }
            : {}
        }}
      >
        {panelContent}
      </Drawer>
    </>
  );
}
